import React, { useEffect, useContext, useState } from 'react';
import PropTypes from 'prop-types';
import equal from 'fast-deep-equal/es6';
import { withRouter } from '../../WithRouter';
import { ProfileContext } from '../../../context/Profile.context';
import { createID, sortList } from '../../../utils/helper';
import { isServiceProviderComplete } from '../../../utils/completes';
import { countries, entities, errors, routes, verifiedSectionKeys, urls, reportingStandards } from '../../../constants';
import { providers } from '../../../constants/forms/serviceProviders';
import ServiceProviderSearch from '../../../containers/ServiceProviderSearch';
import Controls from '../../Controls';
import SectionNextActions from '../../SectionNextActions';
import { PhoneInputField } from '../../PhoneInputField';
import { isCanada, isUSA, isEmail, isWebsite } from '../../../utils/validations';
import { countryList, stateList } from '../../../utils/locale';
import { Landmark, User, UserCheck, Trash2, Info as InfoIcon, CircleAlert } from 'lucide-react';
import { Box, Button, Card, CardContent, CardHeader, CardTitle, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, EntityLabel, EntityLabelGroup, Label, InfoPanel, SectionTitle, SelectField, TextField, Tooltip, Typography } from '@otcmarketsgroup/otcm-ui-lib';

const providerEmptyObj = {
  name: null,
  phone: null,
  website: null,
  email: null,
  address1: null,
  address2: null,
  address3: null,
  city: null,
  state: null,
  zip: null,
  isOwnTransferAgent: null,
  country: countries.US,
  countryId: countries.US,
  noOutsideCounsel: null,
  primaryFirstName: null,
  primaryMiddleName: null,
  primaryLastName: null,
  primaryContactTitle: null,
  primaryContactPhone: null,
  primaryContactEmail: null,
  verifiedSharesAuth: false
};

const ServiceProviders = ({ params: { symbol }, saveApplication }) => {
  const [profileState, dispatchProfile] = useContext(ProfileContext);
  const [dialog, setDialog] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [providerView, setProviderView] = useState(null);
  const [openRemoveDialog, setOpenRemoveDialog] = useState(null);
  const [validationError, setValidationError] = useState(false);
  const profileData = profileState.profile;
  const backupProfile = profileState.backupProfile;
  const isAdd = !providerView || !providerView.id;
  const isTransferAgent = selectedProvider && selectedProvider.typeId === '6' ? true : false;
  const isSecurityCounsels = selectedProvider && selectedProvider.typeId === '3' ? true : false;
  const isPreExisting = providerView && providerView.isPreExisting;
  const incorporationList = profileData && profileData.companyInfo && profileData.companyInfo.incorporationInfo;
  const sortedList = sortList(incorporationList, 'yearOfIncorporation', 'desc');
  const showAuthoirzeTA = isTransferAgent && !isPreExisting && sortedList && sortedList[0] && sortedList[0].countryOfIncorporation && (isUSA(sortedList[0].countryOfIncorporation) || isCanada(sortedList[0].countryOfIncorporation));
  const showActsOwnTA = isTransferAgent && profileData.transferAgents && !profileData.transferAgents.find(ta => ta.isOwnTransferAgent && !ta.isDeleted);
  const showNoSecCounsels = isSecurityCounsels && profileData.securityCounsels && !profileData.securityCounsels.find(sc => sc.noOutsideCounsel && !sc.isDeleted);
  const profileTier = profileData.companyTier.id;
  const edgarFilingStatus = profileData.edgarFilingStatus;
  const isInternationalReporting = edgarFilingStatus && edgarFilingStatus.includes(reportingStandards.INTERNATIONAL) ? true : false;
  const isOTCQX = profileTier === 1 || profileTier === 2 || profileTier === 5 || profileTier === 6;
  const isOTCQB = profileTier === 10;
  const isReadOnly = profileState.readOnly;
  const serviceProviderFormObj = providerView || providerEmptyObj;
  const isNoOutsideCounsel = !!serviceProviderFormObj.noOutsideCounsel;
  const filteredStates = stateList.filter(option => option.countryId === serviceProviderFormObj.countryId);
  const serviceProviderDialogTitle = selectedProvider ? `${isAdd ? 'Add ' : ''}${selectedProvider.title}` : 'Service Provider';

  useEffect(() => {
    if (selectedProvider) {
      setDialog(true);
    }
  }, [selectedProvider]);

  useEffect(() => {
    if (!dialog) {
      setValidationError(false);
      setProviderView(null);
      setSelectedProvider(null);
    }
  }, [dialog]);

  useEffect(() => {
    let isProviderChange = false;

    providers.forEach(provider => {
      if (!equal({ ...backupProfile[provider.key] }, { ...profileData[provider.key] })) isProviderChange = true;
      return;
    });

    if (isProviderChange) {
      saveApplication();
    }
  }, [backupProfile, profileData, saveApplication]);

  const onSearchChange = (value, field) => {
    const updatedObj = { ...providerView };
    updatedObj[field] = value;

    setProviderView(updatedObj);
  };

  const onOwnTaChange = checked => {
    const updatedObj = { ...providerView };

    if (checked) updatedObj.name = null;

    updatedObj.isOwnTransferAgent = checked;

    setProviderView(updatedObj);
  };

  const onNoSecCounselChange = checked => {
    let updatedObj = { ...providerView };

    if (checked) updatedObj = {
      ...providerEmptyObj
    };

    updatedObj.noOutsideCounsel = checked;

    setProviderView(updatedObj);
  };

  const onCheckboxChanged = (value, field) => {
    const updatedObj = { ...providerView };
    updatedObj[field] = value;

    onValueChanged(updatedObj);
  };

  const onValueChanged = updatedObj => {
    if (providerView && providerView.countryId !== updatedObj.countryId) updatedObj.state = null;
    setProviderView(updatedObj);
  };

  const onFieldChange = (field, value) => {
    onValueChanged({ ...serviceProviderFormObj, [field]: value });
  };

  const onPhoneValueChanged = (phone, data) => {
    onValueChanged({
      ...serviceProviderFormObj,
      phone,
      phoneCountryCode: data?.countryCode ? data.countryCode.toUpperCase() : serviceProviderFormObj.phoneCountryCode
    });
  };

  const handleEdit = (data, provider) => {
    setSelectedProvider({ ...provider });
    setProviderView(data);
  };

  const handleRemove = (data, type) => {
    const provider = type.key;
    onUpdate(profileData[provider].map(item => {
      if (item.id === data.id) item.isDeleted = true;
      return item;
    }), provider);
  };

  const handleRemoveClick = (data, provider) => {
    setOpenRemoveDialog({ data, provider });
  };

  const confirmRemoveRow = () => {
    if (!openRemoveDialog) return;

    handleRemove(openRemoveDialog.data, openRemoveDialog.provider);
    setOpenRemoveDialog(null);
  };

  const onUpdate = (data, field) => {
    dispatchProfile({
      type: 'UPDATE_PROFILE',
      payload: data,
      field: field
    });
  };

  const handleUpdate = _ => {
    setValidationError(false);

    const provider = selectedProvider.key;

    if (!isServiceProviderComplete(providerView, selectedProvider.typeId)) {
      setValidationError(true);
      return;
    }

    let updateProvderList = profileData[provider];

    updateProvderList = updateProvderList.map(provider => {
      if (provider.id === providerView.id) return providerView;

      return provider;
    });

    onUpdate(updateProvderList, provider);

    setDialog(false);
  };

  const handleAdd = _ => {
    setValidationError(false);

    const provider = selectedProvider.key;
    const newProvider = providerView;

    if (!isServiceProviderComplete(providerView, selectedProvider.typeId)) {
      setValidationError(true);
      return;
    }

    const id = createID(profileData[provider] ? profileData[provider] : []);
    newProvider.id = id;
    onUpdate([...profileData[provider] || [], newProvider], provider);
    setDialog(false);
  };

  const entityTitle = provider => {
    if (provider.noOutsideCounsel) return 'Internal Securities Counsel';
    if (provider.isOwnTransferAgent) return 'Company Acts as its own Transfer Agent';
    const title = provider.name;

    if (!title || title.trim().length === 0) return entities.NO_NAME;
    return title;
  };

  const hasProvider = key => {
    const hasProvider = profileData[key].filter(data => !data.isDeleted).length > 0 ? true : false;
    const isTransferAgent = key === 'transferAgents';
    const isAuditor = key === 'auditors' && (isOTCQX || isOTCQB);
    const isSecurityCounsels = key === 'securityCounsels' && (isOTCQX || isOTCQB) && !isInternationalReporting;

    if (isTransferAgent || isAuditor || isSecurityCounsels) {
      return hasProvider;
    }

    return true;
  };

  const renderProviders = () => {
    return <Box display='flex' flexDirection='column' gap={3}>
      {providers.map((provider, i) => {
        const list = profileData[provider.key].filter(item => !item.isDeleted);
        const showAddAction = (provider.max && list ? list.length < provider.max : true) && !isReadOnly && provider.addTitle;
        const showIncomplete = provider.isRequired && (provider.key === 'transferAgents' ? !isInternationalReporting : true) && !hasProvider(provider.key);
        const incompleteMessage = `Incomplete Information. Need at least 1 complete ${provider.title}.`;

        const onRemoveClick = (data) => handleRemoveClick(data, provider);

        return <Box key={i}>
          <Tooltip dark disableHoverListener={!showIncomplete} subtitle={incompleteMessage} placement='top-start'>
            <Box display='flex' justifyContent='space-between' alignItems='center' sx={{ borderBottom: showIncomplete ? '3px solid' : 'none', borderColor: 'var(--otc-warning)', mb: showIncomplete ? 2 : 0 }}>
              <Box display='flex' alignItems='center' gap={1}>
                {showIncomplete && <CircleAlert color='var(--otc-warning)' size={16} />}
                <SectionTitle noUnderline>
                  {provider.title}
                </SectionTitle>
                {provider.moreInfo && <Tooltip dark subtitle={provider.moreInfo}>
                  <InfoIcon color='var(--otc-green)' size={14} />
                </Tooltip>}
              </Box>
              {showAddAction && <Button variant='text' onClick={() => setSelectedProvider(provider)}>
                {provider.addTitle}
              </Button>}
            </Box>
          </Tooltip>

          {provider.subTitle && <Typography variant='body2'>
            {provider.subTitle}
          </Typography>}

          {list.length === 0 && <Typography variant='body2'>
            No {provider.title} have been added yet.
          </Typography>}

          <EntityLabelGroup>
            {list.map((item, index) => {
              const isComplete = isServiceProviderComplete(item, provider.typeId);

              return <EntityLabel
                key={`${provider.key}-${item.id || index}`}
                title={entityTitle(item)}
                subtitle={isReadOnly ? 'View' : isComplete ? 'Complete' : 'Enter Information'}
                icon={isComplete ? <UserCheck /> : <User />}
                actionIcon={!isReadOnly ? <Trash2 /> : undefined}
                isComplete={isComplete}
                isWarning={!isComplete}
                onEntityClick={() => handleEdit(item, provider)}
                onActionClick={!isReadOnly ? () => onRemoveClick(item) : undefined} />;
            })}
          </EntityLabelGroup>
        </Box>;
      })
      }
    </Box>;
  };

  const renderProviderForm = () => {
    if (isTransferAgent) return null;

    const disableFirmFields = isNoOutsideCounsel;
    const showReadOnlyPhone = isReadOnly || disableFirmFields;
    const isNameMissing = !disableFirmFields && (!serviceProviderFormObj.name || serviceProviderFormObj.name.trim().length === 0);
    const emailInvalid = !!(serviceProviderFormObj.email && !isEmail(serviceProviderFormObj.email));
    const websiteInvalid = !!(serviceProviderFormObj.website && !isWebsite(serviceProviderFormObj.website));

    return <Box display='flex' flexDirection='column' gap={2}>
      <Typography variant='body1'>
        Provide contact information below.
      </Typography>
      <Box display='grid' gap={2} gridTemplateColumns={{ desktop: 'repeat(6, 1fr)', mobile: '1fr' }}>
        <Box gridColumn={{ desktop: 'span 6', mobile: 'span 1' }}>
          <TextField
            label='Name of Firm'
            placeholder={disableFirmFields ? 'N/A' : 'Name of Firm'}
            readOnly={isReadOnly}
            showClearButton
            maxLength={75}
            disabled={disableFirmFields}
            value={disableFirmFields ? 'N/A' : serviceProviderFormObj.name || ''}
            error={(validationError || isReadOnly) && isNameMissing}
            helperText={validationError && isNameMissing ? errors.INCOMPLETE_DATA_FIELD : ''}
            onChange={e => onFieldChange('name', e.target.value)}
          />
        </Box>

        <Box gridColumn={{ desktop: 'span 3', mobile: 'span 1' }}>
          {showReadOnlyPhone
            ? <TextField
              label='Firm Phone Number (optional)'
              placeholder='N/A'
              readOnly
              disabled={disableFirmFields}
              value={disableFirmFields ? 'N/A' : serviceProviderFormObj.phone || ''}
            />
            : <Box>
              <Label>Firm Phone Number (optional)</Label>
              <PhoneInputField
                value={serviceProviderFormObj.phone || ''}
                country={serviceProviderFormObj.phoneCountryCode?.toLowerCase()}
                onChange={onPhoneValueChanged}
              />
            </Box>}
        </Box>

        <Box gridColumn={{ desktop: 'span 3', mobile: 'span 1' }}>
          <TextField
            label='Firm Email (optional)'
            placeholder={disableFirmFields ? 'N/A' : 'email@mail.com'}
            showClearButton
            readOnly={isReadOnly}
            disabled={disableFirmFields}
            maxLength={75}
            value={disableFirmFields ? 'N/A' : serviceProviderFormObj.email || ''}
            error={emailInvalid}
            helperText={(validationError && emailInvalid) ? errors.INVALID_EMAIL : undefined}
            onChange={e => onFieldChange('email', e.target.value)}
          />
        </Box>

        <Box gridColumn={{ desktop: 'span 6', mobile: 'span 1' }}>
          <TextField
            label='Firm Website (optional)'
            placeholder={disableFirmFields ? 'N/A' : 'Enter text here'}
            showClearButton
            readOnly={isReadOnly}
            maxLength={75}
            disabled={disableFirmFields}
            value={disableFirmFields ? 'N/A' : serviceProviderFormObj.website || ''}
            error={websiteInvalid}
            helperText={(validationError && websiteInvalid) ? errors.INVALID_WEBSITE : undefined}
            onChange={e => onFieldChange('website', e.target.value)}
          />
        </Box>

        <Box gridColumn={{ desktop: 'span 6', mobile: 'span 1' }}>
          <TextField
            label='Firm Address'
            placeholder={disableFirmFields ? 'N/A' : 'Address 1'}
            showClearButton
            readOnly={isReadOnly}
            maxLength={75}
            disabled={disableFirmFields}
            value={disableFirmFields ? 'N/A' : serviceProviderFormObj.address1 || ''}
            onChange={e => onFieldChange('address1', e.target.value)}
          />
        </Box>

        <Box gridColumn={{ desktop: 'span 6', mobile: 'span 1' }}>
          <TextField
            placeholder={disableFirmFields ? 'N/A' : 'Address 2'}
            showClearButton
            readOnly={isReadOnly}
            maxLength={75}
            disabled={disableFirmFields}
            value={disableFirmFields ? 'N/A' : serviceProviderFormObj.address2 || ''}
            onChange={e => onFieldChange('address2', e.target.value)}
          />
        </Box>

        <Box gridColumn={{ desktop: 'span 6', mobile: 'span 1' }}>
          <TextField
            placeholder={disableFirmFields ? 'N/A' : 'Address 3'}
            showClearButton
            readOnly={isReadOnly}
            maxLength={75}
            disabled={disableFirmFields}
            value={disableFirmFields ? 'N/A' : serviceProviderFormObj.address3 || ''}
            onChange={e => onFieldChange('address3', e.target.value)}
          />
        </Box>

        <Box gridColumn={{ desktop: 'span 3', mobile: 'span 1' }}>
          <SelectField
            label='Country'
            readOnly={isReadOnly}
            disabled={disableFirmFields}
            value={disableFirmFields ? '' : serviceProviderFormObj.countryId || ''}
            options={countryList}
            onChange={e => onFieldChange('countryId', e.target.value)}
          />
        </Box>

        <Box gridColumn={{ desktop: 'span 3', mobile: 'span 1' }}>
          <TextField
            label='City'
            placeholder={disableFirmFields ? 'N/A' : 'City'}
            showClearButton
            readOnly={isReadOnly}
            maxLength={75}
            disabled={disableFirmFields}
            value={disableFirmFields ? 'N/A' : serviceProviderFormObj.city || ''}
            onChange={e => onFieldChange('city', e.target.value)}
          />
        </Box>

        <Box gridColumn={{ desktop: 'span 3', mobile: 'span 1' }}>
          <SelectField
            label='State/Province'
            readOnly={isReadOnly}
            disabled={disableFirmFields}
            value={disableFirmFields ? '' : serviceProviderFormObj.state || ''}
            options={filteredStates}
            onChange={e => onFieldChange('state', e.target.value)}
          />
        </Box>

        <Box gridColumn={{ desktop: 'span 3', mobile: 'span 1' }}>
          <TextField
            label='Postal Code'
            placeholder={disableFirmFields ? 'N/A' : 'Postal Code'}
            showClearButton
            readOnly={isReadOnly}
            maxLength={75}
            disabled={disableFirmFields}
            value={disableFirmFields ? 'N/A' : serviceProviderFormObj.zip || ''}
            onChange={e => onFieldChange('zip', e.target.value)}
          />
        </Box>
      </Box>
    </Box>;
  };

  return <Box display='flex' flexDirection='column' gap={2}>
    <Card>
      <CardHeader>
        <CardTitle icon={<Landmark />}>Service Providers</CardTitle>
      </CardHeader>
      <CardContent>
        {renderProviders()}
      </CardContent>
    </Card>
    <SectionNextActions
      readOnly={isReadOnly}
      section={verifiedSectionKeys.SERVICE_PROVIDERS}
      linkTo={`${routes.PROFILE_PREFIX}/${symbol}/${routes.DESIGNATE_CONTACTS}/primary-contacts`}
    />
    <Dialog open={dialog} onClose={() => setDialog(false)}>
      <DialogTitle title={serviceProviderDialogTitle} onClose={() => setDialog(false)} />
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {selectedProvider && <div>
            {selectedProvider.key === 'transferAgents' && <p>
              Companies incorporated in the U.S. or Canada must retain a transfer agent that participates in the <a href={urls.TAVSP} target='_blank' rel='noopener noreferrer'>Transfer Agent Verified Shares Program</a>.
            </p>}
            {(showActsOwnTA && isAdd) && <Box mt={1} mb={1}>
              <Checkbox
                label='The Company Acts as its own Transfer Agent.'
                checked={!!(providerView && providerView.isOwnTransferAgent)}
                disabled={isReadOnly}
                onChange={e => onOwnTaChange(e.target.checked)}
              />
            </Box>}
            {isTransferAgent && <ServiceProviderSearch
              typeId={selectedProvider.typeId}
              error={validationError && !providerView?.isOwnTransferAgent && (!providerView?.name || providerView?.name.trim().length === 0)}
              isRequired={providerView && !providerView.isOwnTransferAgent}
              notAvailable={providerView && providerView.isOwnTransferAgent}
              readOnly={isReadOnly || selectedProvider.isPreExisting}
              value={providerView && providerView.name || null}
              disabled={providerView && providerView.isOwnTransferAgent}
              onValueChanged={value => onSearchChange(value, 'name')}
            />}
            {(showNoSecCounsels && isAdd) && <Box mt={1} mb={1}>
              <Checkbox
                label='The Company does not currently have an outside securities counsel.'
                checked={!!(providerView && providerView.noOutsideCounsel)}
                disabled={isReadOnly}
                onChange={e => onNoSecCounselChange(e.target.checked)}
              />
            </Box>}
            {(isSecurityCounsels && providerView && providerView.noOutsideCounsel) && <InfoPanel title='Internal General Counsel should be listed as an Officer/Contact in the People section.' />}
          </div>}
          {renderProviderForm()}
          {showAuthoirzeTA && <Box mt={1}>
            <Checkbox
              label={`The Company has authorized this transfer agent to provide to OTC Markets Group, upon its request, information related to the Company's securities, including, but not limited to, shares authorized, shares issued and outstanding, and share issuance history.`}
              checked={!!(providerView && providerView.isAuthorized)}
              disabled={isReadOnly}
              onChange={e => onCheckboxChanged(e.target.checked, 'isAuthorized')}
            />
          </Box>}
          {validationError && <InfoPanel isWarning title={errors.INCOMPLETE_DATA} />}
        </Box>
      </DialogContent>
      {!isReadOnly && <Controls
        className='mtXXL'
        cancelText='Cancel'
        submitText={isAdd ? 'Add' : 'Update'}
        onCancelClick={() => setDialog(false)}
        onSubmitClick={isAdd ? handleAdd : handleUpdate}
      />}
    </Dialog>
    <Dialog open={!!openRemoveDialog} onClose={() => setOpenRemoveDialog(null)}>
      <DialogTitle title='Remove Service Provider' onClose={() => setOpenRemoveDialog(null)} />
      <DialogContent>
        <Typography variant='body1' component='p' gutterBottom>
          Are you sure you want to delete?
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button variant='outlined' color='warning' onClick={() => setOpenRemoveDialog(null)}>
          Cancel
        </Button>
        <Button color='warning' onClick={confirmRemoveRow}>
          Yes
        </Button>
      </DialogActions>
    </Dialog>
  </Box>;
};

ServiceProviders.propTypes = {
  params: PropTypes.shape({
    symbol: PropTypes.string
  }),
  saveApplication: PropTypes.func
};

export default withRouter(ServiceProviders);
